/**
 * Authentication Service
 *
 * EAA-1762: password login (staff + tenant-portal) is retired in favor of
 * Keycloak OIDC (Authorization Code + PKCE) — see `@/auth/authClient`. This
 * class keeps its PRE-EXISTING public API (`getAccessToken()`,
 * `getStoredUser()`, `getAuthMode()`, `isAuthenticated()`, `logout()`,
 * `refreshToken()`, `getCurrentUser()`, `clearAuth()`) so the ~10 call sites
 * across the app (api client, upload components, ThemeEditor, ...) need no
 * change — only what backs these methods changed, from localStorage
 * JWT/refresh-token pairs to the OIDC access token held by `authClient`.
 *
 * EAA-1762 addendum: `authConfig.mode === 'password'` (set via VITE_AUTH_MODE)
 * restores that retired staff + tenant-portal username/password paths as an
 * environment-scoped fallback — UAT has no deployed Keycloak/portal, so its
 * build sends every /login to an unreachable localhost OIDC authority. The
 * backend never removed either JWT login endpoint (`POST /auth/login`,
 * `POST /tenants/portal-login` — `domains/portal/tenants/views/auth.py` /
 * `views.py`), so every method below just branches on `authConfig.mode`
 * instead of the OIDC path always winning. Default (`'oidc'`) behavior is
 * byte-for-byte unchanged.
 *
 * Embed iframe (`/embed/*`) is untouched: it still uses API tokens held only
 * in memory (`?token=`), never persisted here — see `utils/embedApiToken.ts`.
 */

import { isJwtExpiring } from '@ai-content-hub/hooks';

import { apiClient } from './api/client';
import { authClient, type EmgAuthLoginOptions } from '@/auth/authClient';
import { authConfig } from '@/auth/authConfig';

export type AuthMode = 'staff' | 'tenant' | 'embed_api';

export interface TenantSummary {
    id: string;
    slug: string;
    name: string;
}

export interface User {
    id: number | string;
    username: string;
    email: string;
    is_superuser: boolean;
    is_staff: boolean;
    tenant?: TenantSummary;
}

export interface LoginRequest {
    username: string;
    password: string;
}

/** EAA-1762 addendum: tenant-portal password login payload (restored for password mode). */
export interface TenantPortalLoginRequest {
    tenant_slug: string;
    username: string;
    password: string;
}

interface LoginResponse {
    access: string;
    refresh: string;
    user: User;
}

interface RefreshResponse {
    access: string;
    refresh?: string;
}

interface PortalLoginEnvelope {
    access: string;
    refresh: string;
    token_type: string;
    expires_in: number;
    user: { id: number; username: string; email: string };
    tenant: TenantSummary;
}

interface PortalMeEnvelope {
    user: { id?: string | number; username?: string; email?: string };
    tenant: TenantSummary & Record<string, unknown>;
    effective_permissions: string[];
}

interface TenantRefreshResponse {
    access: string;
}

/**
 * EAA-1762: the result of one refresh attempt. `refreshToken()` collapsed every
 * failure into `null`, and every caller read `null` as "the session is over" —
 * so a request timeout, an offline moment on laptop wake, or a 5xx from a cold
 * backend destroyed a session whose refresh token was still valid for days
 * (tenant refresh tokens live 7 days; access tokens 60 minutes). The two cases
 * have to be told apart: only the server rejecting the refresh token ends a
 * session, everything else is worth retrying.
 */
export type RefreshOutcome =
    | { status: 'refreshed'; token: string }
    /** The refresh credential is gone or the server rejected it — sign-in required. */
    | { status: 'session-expired' }
    /** Could not reach a verdict (offline, timeout, 5xx). The session may still be fine. */
    | { status: 'unavailable'; message: string };

/**
 * A refresh failure is only definitive when the SERVER said so. `ApiError.status`
 * is absent for network errors and timeouts (see api/client.ts's AbortError
 * branch), and a 5xx is the backend failing, not the credential.
 */
function isDefinitiveRejection(error: unknown): boolean {
    const status = (error as { status?: unknown } | null)?.status;
    return typeof status === 'number' && status >= 400 && status < 500;
}

function unavailable(error: unknown): RefreshOutcome {
    const message =
        (error as { message?: unknown } | null)?.message ?? 'Could not reach the server';
    return { status: 'unavailable', message: String(message) };
}

const USER_KEY = 'auth_user';
const ACCESS_TOKEN_KEY = 'auth_access_token';
const REFRESH_TOKEN_KEY = 'auth_refresh_token';
/** EAA-1762 addendum: password-mode-only — which password path is currently signed in. */
const AUTH_MODE_KEY = 'auth_mode';
const TENANT_ACCESS_TOKEN_KEY = 'tenant_access_token';
const TENANT_REFRESH_TOKEN_KEY = 'tenant_refresh_token';

class AuthService {
    private basePath = '/auth';
    private tenantsPath = '/tenants';
    private refreshPromise: Promise<RefreshOutcome> | null = null;

    private get usesPasswordAuth(): boolean {
        return authConfig.mode === 'password';
    }

    /**
     * EAA-1762: OIDC is the only non-embed auth mode, so this always resolves
     * 'staff' there — kept as a method (not a constant) since call sites
     * (e.g. ThemeEditorPage) still branch on it. In password mode (addendum),
     * reads which of the two password paths (staff / tenant-portal) is signed in.
     */
    getAuthMode(): 'staff' | 'tenant' {
        if (!this.usesPasswordAuth) {
            return 'staff';
        }
        return localStorage.getItem(AUTH_MODE_KEY) === 'tenant' ? 'tenant' : 'staff';
    }

    private setPasswordAuthMode(mode: 'staff' | 'tenant'): void {
        localStorage.setItem(AUTH_MODE_KEY, mode);
    }

    /** Kick off the Authorization Code + PKCE redirect to Keycloak. */
    async login(returnTo?: string, options?: EmgAuthLoginOptions): Promise<void> {
        await authClient.login(returnTo, options);
    }

    /** Password-mode fallback: POST username/password, store the JWT pair. */
    async loginWithPassword(credentials: LoginRequest): Promise<User> {
        const response = await apiClient.post<LoginResponse>(`${this.basePath}/login`, credentials, {
            skipAuth: true,
        });
        const { access, refresh, user } = response.data;
        this.clearTenantTokens();
        this.setPasswordAuthMode('staff');
        this.setTokens(access, refresh);
        this.setStoredUser(user);
        return user;
    }

    /** EAA-1762 addendum: tenant-portal password login (restored for password mode). */
    async loginTenantPortal(credentials: TenantPortalLoginRequest): Promise<User> {
        const response = await apiClient.post<PortalLoginEnvelope>(
            `${this.tenantsPath}/portal-login`,
            credentials,
            { skipAuth: true }
        );
        const { access, refresh, user, tenant } = response.data;
        this.clearStaffTokens();
        this.setPasswordAuthMode('tenant');
        localStorage.setItem(TENANT_ACCESS_TOKEN_KEY, access);
        localStorage.setItem(TENANT_REFRESH_TOKEN_KEY, refresh);

        const mapped: User = {
            id: user.id,
            username: user.username,
            email: user.email || '',
            is_superuser: false,
            is_staff: false,
            tenant,
        };
        this.setStoredUser(mapped);
        return mapped;
    }

    /** RP-initiated logout — redirects to Keycloak, then back to `/login`. */
    async logout(): Promise<void> {
        if (this.usesPasswordAuth) {
            if (this.getAuthMode() === 'tenant') {
                // EAA-1762 addendum: tenant-portal logout was always local-only —
                // no server-side session to end, unlike the staff JWT pair below.
                this.clearTenantTokens();
                this.clearStoredUser();
                this.setPasswordAuthMode('staff');
                return;
            }
            await this.logoutPassword();
            return;
        }
        this.clearStoredUser();
        await authClient.logout();
    }

    private async logoutPassword(): Promise<void> {
        const refresh = this.getStaffRefreshToken();
        try {
            if (refresh) {
                await apiClient.post(`${this.basePath}/logout`, { refresh }, { skipAuth: true });
            }
        } catch {
            // Best-effort — a dead refresh token shouldn't block clearing local state.
        } finally {
            this.clearAuth();
        }
    }

    /**
     * Best-effort silent renew via the OIDC session (used by 401 retry paths).
     * Kept for the ~10 existing call sites: `null` still means "no usable
     * token". Callers that must distinguish an ended session from an
     * unreachable server use {@link refreshAccessToken} instead.
     */
    async refreshToken(): Promise<string | null> {
        const outcome = await this.refreshAccessToken();
        return outcome.status === 'refreshed' ? outcome.token : null;
    }

    /** Refresh once, deduped across concurrent callers, reporting WHY it failed. */
    async refreshAccessToken(): Promise<RefreshOutcome> {
        if (!this.usesPasswordAuth) {
            const token = await authClient.refreshToken();
            return token ? { status: 'refreshed', token } : { status: 'session-expired' };
        }
        // A single in-flight refresh per tab: an idle tab waking up fires every
        // pending query at once, and each one would otherwise POST its own
        // refresh. Harmless for tenant tokens (no rotation) but fatal for the
        // staff path, where /auth/refresh rotates and the loser of the race
        // presents a token the server has already retired.
        if (this.refreshPromise) {
            return this.refreshPromise;
        }
        this.refreshPromise =
            this.getAuthMode() === 'tenant' ? this.doTenantRefresh() : this.doStaffRefresh();
        try {
            return await this.refreshPromise;
        } finally {
            this.refreshPromise = null;
        }
    }

    /**
     * Refresh BEFORE a request when the held token is spent, instead of letting
     * the request 401 first. Callers pass `skipAuth` for the refresh/login
     * endpoints themselves, so this cannot recurse.
     */
    async ensureFreshAccessToken(): Promise<void> {
        if (!this.usesPasswordAuth) return; // OIDC renews via UserManager
        const token = this.getAccessToken();
        if (!token || !isJwtExpiring(token)) return;
        // A failure here is not handled: the request proceeds with the stale
        // token and the 401 path owns the outcome, so there is exactly one place
        // that decides a session has ended.
        await this.refreshAccessToken().catch(() => undefined);
    }

    private async doStaffRefresh(): Promise<RefreshOutcome> {
        const refresh = this.getStaffRefreshToken();
        if (!refresh) {
            return { status: 'session-expired' };
        }
        try {
            const response = await apiClient.post<RefreshResponse>(
                `${this.basePath}/refresh`,
                { refresh },
                { skipAuth: true }
            );
            const { access, refresh: nextRefresh } = response.data;
            if (!access) {
                this.clearAuth();
                return { status: 'session-expired' };
            }
            this.setAccessToken(access);
            if (nextRefresh) {
                this.setRefreshToken(nextRefresh);
            }
            return { status: 'refreshed', token: access };
        } catch (error) {
            if (!isDefinitiveRejection(error)) {
                return unavailable(error);
            }
            this.clearAuth();
            return { status: 'session-expired' };
        }
    }

    private async doTenantRefresh(): Promise<RefreshOutcome> {
        const refresh = localStorage.getItem(TENANT_REFRESH_TOKEN_KEY);
        if (!refresh) {
            return { status: 'session-expired' };
        }
        try {
            const response = await apiClient.post<TenantRefreshResponse>(
                `${this.tenantsPath}/token-refresh`,
                { refresh },
                { skipAuth: true }
            );
            const { access } = response.data;
            if (!access) {
                this.clearAuth();
                return { status: 'session-expired' };
            }
            localStorage.setItem(TENANT_ACCESS_TOKEN_KEY, access);
            return { status: 'refreshed', token: access };
        } catch (error) {
            if (!isDefinitiveRejection(error)) {
                return unavailable(error);
            }
            this.clearAuth();
            return { status: 'session-expired' };
        }
    }

    /**
     * Fetches the current user — OIDC verifies web-content's OIDC access
     * tokens directly against `/auth/me` (see
     * emg_shared.auth.oidc.OIDCAuthentication in the backend repo); password
     * mode hits `/auth/me` (staff) or `/tenants/portal-me` (tenant-portal,
     * EAA-1762 addendum) with the JWT access token instead — and caches the
     * result for `getStoredUser()`.
     */
    async getCurrentUser(): Promise<User> {
        if (this.usesPasswordAuth && this.getAuthMode() === 'tenant') {
            const response = await apiClient.get<PortalMeEnvelope>(`${this.tenantsPath}/portal-me`);
            const { user: u, tenant } = response.data;
            const mapped: User = {
                id: u.id ?? '',
                username: u.username ?? '',
                email: u.email ?? '',
                is_superuser: false,
                is_staff: false,
                tenant: {
                    id: String(tenant.id),
                    slug: tenant.slug,
                    name: tenant.name,
                },
            };
            this.setStoredUser(mapped);
            return mapped;
        }

        try {
            const response = await apiClient.get<User>(`${this.basePath}/me`);
            this.setStoredUser(response.data);
            return response.data;
        } catch (error) {
            if (this.usesPasswordAuth) {
                throw error;
            }
            // Fall back to a minimal identity built from OIDC claims so the app
            // still has *a* user object (e.g. before the backend membership/JIT
            // link settles) rather than throwing checkAuth() into failure.
            //
            // EAA-1886: carry `is_staff`/`is_superuser`/`tenant` over from the last
            // identity /me actually returned. Claims cannot supply them — role never
            // rides the token (D10), so building them fresh means hardcoding `false`,
            // and this branch PERSISTS what it builds. One transient /me failure
            // therefore used to overwrite a correct `is_staff: true` copy with a
            // synthetic non-admin one, and a platform admin lost the tenant switcher,
            // the all-tenants listing and the tenant column for as long as /me kept
            // failing. A synthetic identity must not claim to know less than the
            // fetched one it is standing in for.
            const claims = await authClient.getClaims();
            const known = this.getStoredUser();
            const fallback: User = {
                id: claims?.sub ?? '',
                username: claims?.name ?? claims?.email ?? '',
                email: claims?.email ?? '',
                is_superuser: Boolean(known?.is_superuser),
                is_staff: Boolean(known?.is_staff),
                tenant: known?.tenant,
            };
            this.setStoredUser(fallback);
            return fallback;
        }
    }

    isAuthenticated(): boolean {
        return !!this.getAccessToken();
    }

    /** Synchronous — see `authClient.getAccessTokenSync()` docstring for the OIDC path. */
    getAccessToken(): string | null {
        if (this.usesPasswordAuth) {
            return this.getAuthMode() === 'tenant'
                ? localStorage.getItem(TENANT_ACCESS_TOKEN_KEY)
                : localStorage.getItem(ACCESS_TOKEN_KEY);
        }
        return authClient.getAccessTokenSync();
    }

    private getStaffRefreshToken(): string | null {
        return localStorage.getItem(REFRESH_TOKEN_KEY);
    }

    private setAccessToken(token: string): void {
        localStorage.setItem(ACCESS_TOKEN_KEY, token);
    }

    private setRefreshToken(token: string): void {
        localStorage.setItem(REFRESH_TOKEN_KEY, token);
    }

    private setTokens(access: string, refresh: string): void {
        this.setAccessToken(access);
        this.setRefreshToken(refresh);
    }

    private clearStaffTokens(): void {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
    }

    private clearTenantTokens(): void {
        localStorage.removeItem(TENANT_ACCESS_TOKEN_KEY);
        localStorage.removeItem(TENANT_REFRESH_TOKEN_KEY);
    }

    getStoredUser(): User | null {
        const stored = localStorage.getItem(USER_KEY);
        if (!stored) return null;

        try {
            return JSON.parse(stored) as User;
        } catch {
            return null;
        }
    }

    setStoredUser(user: User): void {
        localStorage.setItem(USER_KEY, JSON.stringify(user));
    }

    private clearStoredUser(): void {
        localStorage.removeItem(USER_KEY);
    }

    clearAuth(): void {
        this.clearStoredUser();
        this.clearStaffTokens();
        this.clearTenantTokens();
        this.setPasswordAuthMode('staff');
    }
}

export const authService = new AuthService();
export default authService;
